// apps/patient/src/services/api.js (patient API service)
import requestQueue, { PRIORITY } from './requestQueue';

const XANO_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://test-api.rehabilitationhealth.com/api:RIB0KqtX';
const XANO_TESTING_BASE_URL = 'https://test-api.rehabilitationhealth.com/api:RIB0KqtX';

// Toggle for -dev auth endpoints (testing new phone-first login against production Xano)
const USE_DEV_AUTH = false;
const authPath = (path) => USE_DEV_AUTH ? `${path}-dev` : path;

import { isDemoEnvironment, getCurrentPlatform } from '@/config/platforms';

// Patient reschedule body (portal and reminder link). The server derives end time/timestamp.
const buildRescheduleBody = (data) => ({
  provider_id: data.provider_id,
  location_id: data.location_id,
  appointment_date: data.appointment_date,
  start_time: data.start_time,
  start_timestamp: data.start_timestamp,
  timezone: data.timezone
});

class PatientApiService {
  constructor() {
    this.baseUrl = XANO_BASE_URL;
  }

  /**
   * Internal fetch wrapper that goes through the request queue
   * @param {string} url - Full URL to fetch
   * @param {Object} fetchOptions - Standard fetch options
   * @param {Object} queueOptions - Queue options (priority, label)
   */
  async _queuedFetch(url, fetchOptions = {}, queueOptions = {}) {
    const { priority = PRIORITY.NORMAL, label = 'patient-api-request' } = queueOptions;

    return requestQueue.enqueue(
      async () => {
        const response = await fetch(url, fetchOptions);

        // Attach status to error for retry logic
        if (!response.ok) {
          const error = new Error(`HTTP error! status: ${response.status}`);
          error.status = response.status;
          error.response = response;
          throw error;
        }

        return response;
      },
      { priority, label }
    );
  }


  // Create request method following provider pattern
  async request(endpoint, options = {}, queueOptions = {}) {
    const token = this.getToken();

    // For auth endpoints, token is optional
    const requiresAuth = !endpoint.startsWith('/auth/patient/send-verification') &&
      !endpoint.startsWith('/auth/patient/verify-pin') &&
      !endpoint.startsWith('/auth/patient/verify-biometric') &&
      !endpoint.startsWith('/auth/patient/resolve-identity');

    if (requiresAuth && !token) {
      throw new Error('No authentication token found');
    }

    try {
      const headers = {};

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Only add Content-Type for non-FormData requests
      if (!(options.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
      }

      // Use queued fetch instead of direct fetch
      const response = await this._queuedFetch(
        `${this.baseUrl}${endpoint}`,
        {
          ...options,
          headers: {
            ...headers,
            ...options.headers
          }
        },
        {
          priority: queueOptions.priority || PRIORITY.NORMAL,
          label: queueOptions.label || `patient:${endpoint}`
        }
      );

      // Handle authentication errors
      if (response.status === 401 || response.status === 403) {
        this.clearToken();
        // Redirect to login
        window.location.href = '/login';
        throw new Error('Authentication failed');
      }

      return await response.json();
    } catch (error) {
      console.error('[PatientAPI] Request failed:', error);
      throw error;
    }
  }

  // Public request - no auth header, no queue (for public/unauthenticated Xano endpoints)
  async publicRequest(endpoint, options = {}) {
    try {
      const headers = {
        'Content-Type': 'application/json',
        ...options.headers
      };

      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers
      });

      if (!response.ok) {
        const error = new Error(`HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.response = response;
        throw error;
      }

      return await response.json();
    } catch (error) {
      console.error('[PatientAPI] Public request failed:', error);
      throw error;
    }
  }

  // Token management
  getToken() {
    return localStorage.getItem('patient_auth_token');
  }

  setToken(token) {
    if (token) {
      localStorage.setItem('patient_auth_token', token);
    }
  }

  clearToken() {
    localStorage.removeItem('patient_auth_token');
  }

  // ==================== AUTH ENDPOINTS ====================

  // Step 1: Send verification code (phone or email)
  async sendVerificationCode({ phone, email }) {
    return this.request(authPath('/auth/patient/send-verification'), {
      method: 'POST',
      body: JSON.stringify({ phone, email })
    });
  }

  // Step 2: Verify PIN code
  async verifyPinCode({ phone, email, code, verificationId }) {
    return this.request(authPath('/auth/patient/verify-pin'), {
      method: 'POST',
      body: JSON.stringify({
        phone,
        email,
        code,
        verification_id: verificationId,
        platform: getCurrentPlatform().id
      })
    });
  }

  // Step 2b: Resolve identity (when verify-pin returns needs_identity)
  async resolveIdentity({ verificationId, sessionToken, firstName, lastName, birthday, phone, email }) {
    return this.request(authPath('/auth/patient/resolve-identity'), {
      method: 'POST',
      body: JSON.stringify({
        verification_id: verificationId,
        session_token: sessionToken,
        first_name: firstName,
        last_name: lastName,
        birthday,
        phone,
        email,
        platform: getCurrentPlatform().id
      })
    });
  }

  // Step 3: Verify biometric
  async verifyBiometric(verificationData) {
    return this.request('/auth/patient/verify-biometric', {
      method: 'POST',
      body: JSON.stringify(verificationData)
    });
  }

  // Get patient profile (same as provider)
  async getMe() {
    return this.request('/auth/me', {
      method: 'GET'
    }, { priority: PRIORITY.CRITICAL, label: 'getMe' });
  }

  async linkProgramToUser(programId, patientId) {

    return this.request('/patients/link-program', {
      method: 'POST',
      body: JSON.stringify({
        //user_id: userProfile.id,
        patient_id: patientId,
        program_id: programId
      })
    });
  }

  async linkWorkoutPlanToUser(workoutPlanId) {
    return this.request('/patients/link-workout-plan', {
      method: 'POST',
      body: JSON.stringify({
        workout_plan_id: workoutPlanId
      })
    });
  }

  // ==================== PATIENT ENDPOINTS ====================

  // Get patient's own details
  async getMyDetails() {
    return this.request('/patient/me', {
      method: 'GET'
    });
  }

  // Update patient profile
  async updateMyProfile(profileData) {
    return this.request('/patients/user', {
      method: 'POST',
      body: JSON.stringify(profileData)
    });
  }

  // Add Patient profile
  async createPatientRecord(profileData) {
    return this.request('/patient', {
      method: 'POST',
      body: JSON.stringify(profileData)
    });
  }

  async updatePatientRecord(patientId, updateData) {
    return this.request(`/patient/${patientId}`, {
      method: 'PATCH',
      body: JSON.stringify(updateData)
    });
  }

  // ==================== PRESCRIPTIONS ====================

  // Get patient's prescriptions
  async getMyPrescriptions(params = {}) {
    const queryParams = new URLSearchParams();

    // Add pagination params
    if (params.page) queryParams.append('page', params.page);
    if (params.perPage) queryParams.append('per_page', params.perPage || '20');
    if (params.status) queryParams.append('status', params.status);

    const queryString = queryParams.toString();
    const endpoint = `/patients/prescriptions${queryString ? `?${queryString}` : ''}`;

    return this.request(endpoint, {
      method: 'GET'
    });
  }

  // Get prescription detail
  async getPrescriptionDetail(prescriptionId) {
    return this.request(`/prescription/${prescriptionId}`, {
      method: 'GET'
    });
  }
  // preferred pharmacy
  async getMyPharmacy() {
    return this.request('/patients/pharmacy', {
      method: 'GET'
    });
  }

  // ==================== APPOINTMENTS ====================

  // Get patient's appointments
  async getMyAppointments() {
    return this.request('/patients/appointments', {
      method: 'GET'
    }, { priority: PRIORITY.NORMAL, label: 'getMyAppointments' });
  }

  // Get appointment detail
  async getAppointmentDetail(appointmentId) {
    return this.request(`/appointment/${appointmentId}`, {
      method: 'GET'
    });
  }

  // Request appointment (legacy - creates appointment directly)
  async requestAppointment(appointmentData) {
    return this.request('/appointment', {
      method: 'POST',
      body: JSON.stringify(appointmentData)
    });
  }

  // Submit appointment request via dedicated endpoint (creates form response, handles both scheduling modes)
  async submitAppointmentRequest(data) {
    return this.request('/patients/appointment-request', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async getPublicWorkoutPlan(workoutPlanId) {
    return this.publicRequest(`/public/workout-plan/${workoutPlanId}`, { method: 'GET' });
  }

  /**
   * Public preview of a base routine (upper | lower | full), rendered from the exercise ladder.
   * Returns the same plan shape as getPublicWorkoutPlan with a single week.
   */
  async getPublicBaseRoutine(routineKey) {
    return this.publicRequest(`/public/base-routine/${encodeURIComponent(routineKey)}`, { method: 'GET' });
  }

  // Public scheduling methods (used by shared AppointmentTimeField)
  async getOrganizationPublic(organizationId) {
    return this.publicRequest(`/public/organizations/${organizationId}`, { method: 'GET' });
  }

  async getOrganizationUsersPublic(organizationId) {
    return this.publicRequest(`/users/public?organization_id=${organizationId}`, { method: 'GET' });
  }

  async getAppointmentTypesPublic(organizationId, { locationId } = {}) {
    const qp = new URLSearchParams({ organization_id: organizationId, is_active: 'true' });
    if (locationId) qp.append('location_id', locationId);
    return this.publicRequest(`/appointment_types/public?${qp.toString()}`, { method: 'GET' });
  }

  async getProviderAvailabilityPublic(providerId, { locationId, organizationId } = {}) {
    const qp = new URLSearchParams({ provider_id: providerId });
    if (organizationId) qp.append('organization_id', organizationId);
    if (locationId) qp.append('location_id', locationId);
    return this.publicRequest(`/public/provider-availability?${qp.toString()}`, { method: 'GET' });
  }

  async getScheduleOverridesPublic(providerId, startDate, endDate, { organizationId } = {}) {
    const params = new URLSearchParams({ provider_id: providerId });
    if (organizationId) params.set('organization_id', organizationId);
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    return this.publicRequest(`/public/schedule-overrides?${params.toString()}`, { method: 'GET' });
  }

  async getAppointmentsPublic(data) {
    const qp = new URLSearchParams({
      provider_id: data.provider_id,
      organization_id: data.organization_id,
      start_date: data.date_range.start,
      end_date: data.date_range.end
    });
    if (data.location_id) qp.append('location_id', data.location_id);
    return this.publicRequest(`/appointments/public?${qp.toString()}`, { method: 'GET' });
  }

  async getOrganizationLocationsPublic(organizationId, { includeProviders = false } = {}) {
    const qp = new URLSearchParams({ organization_id: organizationId });
    if (includeProviders) qp.append('include_providers', 'true');
    return this.publicRequest(`/public/organization-locations?${qp.toString()}`, { method: 'GET' });
  }

  async getUserPublic(userId) {
    return this.publicRequest(`/user/${userId}/public`, { method: 'GET' });
  }

  async getUpcomingAppointmentCount() {
    try {
      const appointments = await this.request('/patients/appointments', {
        method: 'GET'
      }, { priority: PRIORITY.LOW, label: 'getUpcomingAppointmentCount' });

      // Count appointments with future dates and scheduled status
      if (Array.isArray(appointments)) {
        const now = new Date();
        return appointments.filter(apt =>
          new Date(apt.appointment_date) > now &&
          (apt.status === 'scheduled' || apt.status === 'confirmed')
        ).length;
      }
      return 0;
    } catch (error) {
      console.error('[PatientAPI] Error fetching appointment count:', error);
      return 0;
    }
  }

  // Patient-owned cancel (server enforces ownership and the cancellation window)
  async cancelAppointment(appointmentId, reason) {
    return this.request(`/patients/appointments/${appointmentId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ cancellation_reason: reason || null })
    });
  }

  // Manage info for the portal detail/reschedule pages (flags, eligible providers, locations)
  async getAppointmentManageInfo(appointmentId) {
    return this.request(`/patients/appointments/${appointmentId}/manage`, {
      method: 'GET'
    });
  }

  // Public reminder-link manage endpoints (no auth; token in the path)
  async getAppointmentLink(token) {
    return this.publicRequest(`/public/appointment-link/${encodeURIComponent(token)}`, { method: 'GET' });
  }

  async cancelAppointmentByLink(token, reason) {
    return this.publicRequest(`/public/appointment-link/${encodeURIComponent(token)}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ cancellation_reason: reason || null })
    });
  }

  async rescheduleAppointmentByLink(token, rescheduleData) {
    return this.publicRequest(`/public/appointment-link/${encodeURIComponent(token)}/reschedule`, {
      method: 'POST',
      body: JSON.stringify(buildRescheduleBody(rescheduleData))
    });
  }

  // Join the waitlist from the reschedule picker's no-availability notice
  // (body: provider_mode, location_id, time_preferences, earliest_date, latest_date, comments)
  async joinWaitlistByLink(token, body) {
    return this.publicRequest(`/public/appointment-link/${encodeURIComponent(token)}/waitlist`, {
      method: 'POST',
      body: JSON.stringify(body)
    });
  }

  async joinWaitlist(appointmentId, body) {
    return this.request(`/patients/appointments/${appointmentId}/waitlist`, {
      method: 'POST',
      body: JSON.stringify(body)
    });
  }

  // Public waitlist slot offer (no auth; token in the path): the /w/:token page
  async getWaitlistOffer(token) {
    return this.publicRequest(`/public/waitlist-offer/${encodeURIComponent(token)}`, { method: 'GET' });
  }

  async claimWaitlistOffer(token) {
    return this.publicRequest(`/public/waitlist-offer/${encodeURIComponent(token)}/claim`, {
      method: 'POST',
      body: JSON.stringify({})
    });
  }

  // Confirm an appointment
  async confirmAppointment(appointmentId) {
    return this.request(`/appointment/${appointmentId}/confirm`, {
      method: 'POST',
      body: JSON.stringify({
        confirmed_at: new Date().toISOString()
      })
    });
  }
  // Patient-owned reschedule (server checks eligibility, window and slot; derives the end)
  async rescheduleAppointment(appointmentId, rescheduleData) {
    return this.request(`/patients/appointments/${appointmentId}/reschedule`, {
      method: 'POST',
      body: JSON.stringify(buildRescheduleBody(rescheduleData))
    });
  }

  // Get available appointment slots for a provider
  async getAvailableSlots(params = {}) {
    const queryParams = new URLSearchParams();

    if (params.provider_id) queryParams.append('provider_id', params.provider_id);
    if (params.location_id) queryParams.append('location_id', params.location_id);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.appointment_type) queryParams.append('appointment_type', params.appointment_type);
    if (params.duration_minutes) queryParams.append('duration_minutes', params.duration_minutes);

    const queryString = queryParams.toString();
    const endpoint = `/appointments/available-slots${queryString ? `?${queryString}` : ''}`;

    return this.request(endpoint, {
      method: 'GET'
    });
  }

  // Check if appointment can be cancelled (based on policy)
  async checkCancellationPolicy(appointmentId) {
    return this.request(`/appointment/cancellation-policy/${appointmentId}`, {
      method: 'GET'
    });
  }

  // Join telehealth appointment
  async joinTelehealthAppointment(appointmentId) {
    return this.request(`/appointment/${appointmentId}/join-telehealth`, {
      method: 'POST',
      body: JSON.stringify({
        joined_at: new Date().toISOString()
      })
    });
  }

  // Get appointment reminders preferences
  async getAppointmentReminders() {
    return this.request('/patients/appointment-reminders', {
      method: 'GET'
    });
  }

  // Update appointment reminder preferences
  async updateAppointmentReminders(preferences) {
    return this.request('/patients/appointment-reminders', {
      method: 'PATCH',
      body: JSON.stringify(preferences)
    });
  }

  // Get appointment types available for booking
  async getAppointmentTypes() {
    return this.request('/appointments/types', {
      method: 'GET'
    });
  }

  // Get organization's cancellation policy
  async getCancellationPolicy() {
    return this.request('/organization/cancellation-policy', {
      method: 'GET'
    });
  }

  // Check in for appointment
  async checkInForAppointment(appointmentId) {
    return this.request(`/appointment/${appointmentId}/check-in`, {
      method: 'POST',
      body: JSON.stringify({
        checked_in_at: new Date().toISOString(),
        check_in_method: 'patient_portal'
      })
    });
  }

  // Get appointment prep instructions
  async getAppointmentPrep(appointmentType) {
    return this.request(`/appointments/prep-instructions/${appointmentType}`, {
      method: 'GET'
    });
  }

  // ==================== HEALTH RECORDS ====================

  // Get patient's health records/encounters
  async getMyHealthRecords() {
    return this.request('/patient/records', {
      method: 'GET'
    });
  }

  // Get specific health record
  async getHealthRecord(recordId) {
    return this.request(`/patient/records/${recordId}`, {
      method: 'GET'
    });
  }

  // ==================== CLINICAL RECORDS ====================

  // Get patient's encounters with pagination
  async getMyEncounters(params = {}) {
    const queryParams = new URLSearchParams();

    // Add pagination params
    if (params.page) queryParams.append('page', params.page);
    if (params.perPage) queryParams.append('per_page', params.perPage || '20');
    if (params.encounter_type) queryParams.append('encounter_type', params.encounter_type);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);

    const queryString = queryParams.toString();
    const endpoint = `/patients/encounters${queryString ? `?${queryString}` : ''}`;

    return this.request(endpoint, {
      method: 'GET'
    });
  }

  // Get single encounter detail with vitals
  async getEncounterDetail(encounterId) {
    return this.request(`/encounter/${encounterId}`, {
      method: 'GET'
    });
  }

  // Get clinical note for an encounter
  async getClinicalNote(noteId) {
    return this.request(`/clinical_note/${noteId}`, {
      method: 'GET'
    });
  }

  async getClinicalNotes(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.encounter_id) queryParams.append('encounter_id', params.encounter_id);
    const queryString = queryParams.toString();
    return this.request(`/clinical_note${queryString ? `?${queryString}` : ''}`, {
      method: 'GET'
    });
  }

  // Get all clinical notes with pagination
  async getMyClinicalNotes(params = {}) {
    const queryParams = new URLSearchParams();

    // Add pagination params
    if (params.page) queryParams.append('page', params.page);
    if (params.perPage) queryParams.append('per_page', params.perPage || '20');

    const queryString = queryParams.toString();
    const endpoint = `/patients/clinical-notes${queryString ? `?${queryString}` : ''}`;

    return this.request(endpoint, {
      method: 'GET'
    });
  }

  // ==================== EXERCISE PROGRAMS ====================

  // Get patient's exercise prescriptions
  async getMyExercisePrograms() {
    return this.request('/patients/exercises', {
      method: 'GET'
    }, { priority: PRIORITY.NORMAL, label: 'getMyExercisePrograms' });
  }

  // Get specific exercise program - changed this, endpoint wasn't live previously
  async getExerciseProgram(programId) {
    return this.request(`/exercise_prescription/${programId}`, {
      method: 'GET'
    });
  }

  // Get active exercise programs to determine feature visibility
  async getActivePrograms(patientId) {
    return this.request(`/patient/${patientId}/active-programs`, {
      method: 'GET'
    }, { priority: PRIORITY.HIGH, label: 'getActivePrograms' });
  }

  async getActiveProgramCount() {
    try {
      const programs = await this.request('/patients/exercises', {
        method: 'GET'
      }, { priority: PRIORITY.LOW, label: 'getActiveProgramCount' });

      // Count active programs
      if (Array.isArray(programs)) {
        return programs.filter(program =>
          program.status === 'active' &&
          (!program.end_date || new Date(program.end_date) > new Date()) &&
          program.assignment_type != "exercise_prescription" &&
          program.assignment_type != "exercise_now"
        ).length;
      }
      return 0;
    } catch (error) {
      console.error('[PatientAPI] Error fetching program count:', error);
      return 0;
    }
  }
  // ==================== STANDALONE EXERCISES ====================

  async getStandaloneExercises(isActive = null) {
    const params = new URLSearchParams();
    if (isActive !== null) params.append('is_active', isActive);
    const query = params.toString();
    return this.request(`/patients/standalone-exercises${query ? `?${query}` : ''}`, {
      method: 'GET'
    }, { priority: PRIORITY.NORMAL, label: 'getStandaloneExercises' });
  }

  async completeStandaloneExercises(completions, sessionDate = null) {
    return this.request('/patients/standalone-exercises/complete', {
      method: 'POST',
      body: JSON.stringify({
        completions,
        session_date: sessionDate || new Date().toISOString().split('T')[0]
      })
    }, { priority: PRIORITY.HIGH, label: 'completeStandaloneExercises' });
  }

  async getStandaloneExerciseCompletions(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.exercise_id) queryParams.append('exercise_id', params.exercise_id);
    const query = queryParams.toString();
    return this.request(`/patients/standalone-exercises/completions${query ? `?${query}` : ''}`, {
      method: 'GET'
    }, { priority: PRIORITY.NORMAL, label: 'getStandaloneExerciseCompletions' });
  }

  async getExerciseDefaults(exerciseIds) {
    if (!exerciseIds) return [];
    return this.request(`/patients/exercise-defaults?exercise_ids=${encodeURIComponent(exerciseIds)}`, {
      method: 'GET'
    }, { priority: PRIORITY.LOW, label: 'getExerciseDefaults' });
  }

  // Get workout plan details
  async getWorkoutPlan(workoutPlanId) {
    return this.request(`/workout_plan/${workoutPlanId}`, {
      method: 'GET'
    });
  }

  // Get workout plan schedule for a specific week
  async getWorkoutPlanSchedule(workoutPlanId, weekNumber) {
    return this.request(`/workout-plans/${workoutPlanId}/week/${weekNumber}/schedule`, {
      method: 'GET'
    });
  }

  // Get rehab program details
  async getRehabProgram(rehabProgramId) {
    return this.request(`/rehab_program/${rehabProgramId}`, {
      method: 'GET'
    });
  }

  async getExerciseAssignment(assignmentId) {
    return this.request(`/patients/exercise-assignments?assignment_id=${assignmentId}`, {
      method: 'GET'
    });
  }

  async getWorkoutWeekSchedule(workoutPlanId, weekNumber) {
    return this.request(`/workout_plan/${workoutPlanId}/weeks/schedule?week_number=${weekNumber}`, {
      method: 'GET'
    });
  }

  async getRehabPhaseSchedule(rehabProgramId, phaseNumber) {
    return this.request(`/rehab_program/${rehabProgramId}/phases/prescriptions?phase_number=${phaseNumber}`, {
      method: 'GET'
    });
  }

  // Get exercise prescriptions for a date range
  async getExerciseSchedule(startDate, endDate) {
    return this.request('/patient/exercise-schedule', {
      method: 'GET',
      params: {
        start_date: startDate,
        end_date: endDate
      }
    });
  }

  // Create a new session (called when starting a workout)
  async createSession(assignmentId, data) {
    return this.request('/session_completion', {
      method: 'POST',
      body: JSON.stringify({
        assignment_id: assignmentId,
        prescription_id: data.prescriptionId,
        session_date: data.sessionDate,
        started_at: new Date().toISOString(),
        session_type: data.sessionType || 'interactive',
        total_exercises_prescribed: data.totalExercises,
        status: 'in_progress',
        // Location context (optional)
        location_id: data.locationId || null,
        location_name: data.locationName || null,
        equipment_available: data.equipmentAvailable || null,
        substitutions_applied: data.substitutions || []
      })
    });
  }

  // Update session progress (called during workout)
  async updateSession(sessionId, data) {
    return this.request(`/session_completion/${sessionId}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  // Complete session with all exercise data
  async completeSession(sessionId, data) {
    return this.request(`/patients/exercise-sessions/complete`, {
      method: 'POST',
      body: JSON.stringify({
        session_id: sessionId,
        completed_at: new Date().toISOString(),
        exercises_completed: data.exercises_completed,
        exercises_skipped: data.exercises_skipped,
        session_notes: data.session_notes,
        session_rating: data.session_rating,
        status: data.exercises_completed > 0 ? 'completed' : 'partial',

        // Exercise completion records to create
        exercise_completions: data.completed_exercises.map(ex => ({
          ...ex,
          session_id: sessionId
        })),
        skipped_exercises: data.skipped_exercises
      })
    });
  }

  // Get sessions for an assignment (for history/progress)
  async getAssignmentSessions(assignmentId, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = `/session_completion?assignment_id=${assignmentId}${queryString ? `&${queryString}` : ''}`;

    return this.request(endpoint, {
      method: 'GET'
    });
  }

  // Get specific session details
  async getSession(sessionId) {
    return this.request(`/session_completion/${sessionId}`, {
      method: 'GET'
    });
  }

  // Get exercise completions for a session
  async getSessionCompletions(sessionId) {
    return this.request(`/patients/exercise-sessions/completions?session_id=${sessionId}`, {
      method: 'GET'
    });
  }

  // Updated log exercise session to use new structure
  async logExerciseSession(assignmentId, sessionData) {
    // This creates both session_completion and exercise_completion records
    return this.request('/patients/exercise-sessions/log', {
      method: 'POST',
      body: JSON.stringify({
        assignment_id: assignmentId,
        prescription_id: sessionData.prescription_id,
        session_date: sessionData.session_date,
        session_type: sessionData.session_type || 'interactive',
        started_at: sessionData.started_at || new Date().toISOString(),
        completed_at: new Date().toISOString(),

        // Session summary
        total_exercises_prescribed: sessionData.total_exercises_prescribed,
        exercises_completed: sessionData.completed_exercises?.length || 0,
        exercises_skipped: sessionData.skipped_exercises?.length || 0,
        session_notes: sessionData.session_notes,
        session_rating: sessionData.session_rating,
        status: sessionData.completed_exercises?.length > 0 ? 'completed' : 'partial',

        // Exercise details
        exercise_completions: sessionData.completed_exercises,
        skipped_exercises: sessionData.skipped_exercises
      })
    });
  }

  // Get last session for "copy from previous" feature
  async getLastSessionForAssignment(assignmentId) {
    return this.request(`/patients/exercise-assignments/last-session?assignment_id=${assignmentId}`, {
      method: 'GET'
    });
  }

  // Helper to determine current week/phase for an assignment
  async getAssignmentProgress(assignmentId) {
    return this.request(`/patients/exercise-assignments/progress?assignment_id=${assignmentId}`, {
      method: 'GET'
    });
  }

  // Get rehab program prescriptions for a phase
  async getRehabProgramPhase(rehabProgramId, phaseNumber) {
    return this.request(`/rehab-programs/${rehabProgramId}/phase/${phaseNumber}`, {
      method: 'GET'
    });
  }

  // ==================== FORMS & ASSESSMENTS ====================

  // Get pending forms
  async getMyPendingForms() {
    return this.request('/patients/forms/pending', {
      method: 'GET'
    });
  }

  // Submit form response
  async submitFormResponse(formId, responseData) {
    return this.request(`/patients/forms/${formId}/submit`, {
      method: 'POST',
      body: JSON.stringify(responseData)
    });
  }

  async getSchedulingForm() {

    return this.request(`/patients/scheduling-form`, {
      method: 'GET'
    });
  }

  // ==================== FORM RESPONSES ====================

  // Get patient's form responses
  async getMyFormResponses() {
    return this.request('/patients/form-responses', {
      method: 'GET'
    });
  }

  // Get specific form response detail
  async getFormResponse(responseId) {
    return this.request(`/form-responses/${responseId}`, {
      method: 'GET'
    });
  }

  // Get form definition for a response
  async getFormDefinition(formId) {
    return this.request(`/forms/${formId}`, {
      method: 'GET'
    });
  }

  // ==================== MESSAGING SYSTEM ====================

  // Thread Management
  async getMessageThreads(filters = {}) {
    const queryParams = new URLSearchParams();

    // Patient-specific filters
    if (filters.category?.length) filters.category.forEach(cat => queryParams.append('category', cat));
    if (filters.status?.length) filters.status.forEach(stat => queryParams.append('status', stat));
    if (filters.unread_only !== undefined) queryParams.append('unread_only', filters.unread_only);
    if (filters.has_attachments !== undefined) queryParams.append('has_attachments', filters.has_attachments);
    if (filters.page) queryParams.append('page', filters.page);
    if (filters.limit) queryParams.append('limit', filters.limit || 20);
    if (filters.sort_by) queryParams.append('sort_by', filters.sort_by || 'last_message_at');
    if (filters.sort_direction) queryParams.append('sort_direction', filters.sort_direction || 'desc');


    return this.request(`/patients/message_thread?${queryParams.toString()}`, {
      method: 'GET'
    }, 'getMessageThreads');
  }

  async getMessageThread(threadId) {
    return this.request(`/message_thread/${threadId}`, {
      method: 'GET'
    }, 'getMessageThread');
  }

  async createMessageThread(threadData) {
    return this.request('/message_thread', {
      method: 'POST',
      body: JSON.stringify(threadData)
    }, 'createMessageThread');
  }

  async updateMessageThread(threadId, updates) {
    return this.request(`/message_thread/${threadId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        ...updates,
        updated_at: Date.now()
      })
    }, 'updateMessageThread');
  }

  // Messages

  async getMyMessages() {
    return this.request('/patients/messages', {
      method: 'GET'
    }, { priority: PRIORITY.NORMAL, label: 'getMyMessages' });
  }

  async getMessages(threadId, pagination = {}) {
    const queryParams = new URLSearchParams();
    if (pagination.page) queryParams.append('page', pagination.page);
    if (pagination.limit) queryParams.append('limit', pagination.limit || 50);
    if (pagination.before) queryParams.append('before', pagination.before);
    if (pagination.after) queryParams.append('after', pagination.after);

    return this.request(`/message_thread/${threadId}/messages?${queryParams.toString()}`, {
      method: 'GET'
    }, 'getMessages');
  }

  // Updated sendMessage to replace existing basic version
  async sendMessage(threadId, messageData) {
    return this.request(`/message`, {
      method: 'POST',
      body: JSON.stringify({
        thread_id: threadId,
        sender_id: messageData.sender_id,
        sender_name: messageData.sender_name,
        body: messageData.body,
        body_format: messageData.body_format || 'plain',
        is_urgent: messageData.is_urgent || false,
        parent_message_id: messageData.parent_message_id,
        attachments: messageData.attachments || [],
        created_at: Date.now(),
        delivery_status: 'pending'
      })
    }, 'sendMessage');
  }

  async editMessage(messageId, content) {
    return this.request(`/message/${messageId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        body: content,
        edited: true,
        edited_at: Date.now()
      })
    }, 'editMessage');
  }

  async updateMessage(messageId, updates) {
    return this.request(`/message/${messageId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        ...updates,
        updated_at: Date.now()
      })
    }, 'updateMessage');
  }

  async deleteMessage(messageId) {
    return this.request(`/message/${messageId}`, {
      method: 'DELETE'
    }, 'deleteMessage');
  }

  async markMessageRead(messageId) {
    return this.request(`/message/${messageId}/read`, {
      method: 'POST',
      body: JSON.stringify({
        read_at: Date.now()
      })
    }, 'markMessageRead');
  }

  async markThreadRead(threadId) {
    return this.request(`/message_thread/${threadId}/read`, {
      method: 'POST',
      body: JSON.stringify({
        read_at: Date.now()
      })
    }, 'markThreadRead');
  }

  // Search'
  async searchMessages(query, filters = {}) {
    const queryParams = new URLSearchParams();
    queryParams.append('q', query);
    if (filters.thread_id) queryParams.append('thread_id', filters.thread_id);
    if (filters.category) queryParams.append('category', filters.category);
    if (filters.date_from) queryParams.append('date_from', filters.date_from);
    if (filters.date_to) queryParams.append('date_to', filters.date_to);
    if (filters.limit) queryParams.append('limit', filters.limit || 20);

    return this.request(`/messages/search?${queryParams.toString()}`, {
      method: 'GET'
    }, 'searchMessages');
  }

  async searchThreads(query, filters = {}) {
    const queryParams = new URLSearchParams();
    queryParams.append('q', query);
    if (filters.category) queryParams.append('category', filters.category);
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.limit) queryParams.append('limit', filters.limit || 20);

    return this.request(`/messages/search?${queryParams.toString()}`, {
      method: 'GET'
    }, 'searchThreads');
  }

  // Updated unread count method (replaces existing getUnreadMessageCount)
  async getUnreadMessageCount() {
    return this.request(`/message_threads/unread-count`, {
      method: 'GET'
    }, { priority: PRIORITY.LOW, label: 'getUnreadMessageCount' });
  }

  // Get attachment download URL - environment aware
  async getAttachmentDownloadUrl(path) {
    // In demo environment, the path is already a public URL
    if (isDemoEnvironment()) {
      return path;
    }

    // In production, make the API request for private file access
    try {
      const response = await this.request(`/attachment/download-url?path=${path}&ttl=1800`);
      return response;
    } catch (error) {
      console.error('[PatientAPI] Failed to generate download URL:', error);
      throw new Error('Failed to generate download URL');
    }
  }

  // Download attachment - environment aware
  async downloadAttachment(attachment) {
    try {
      let downloadUrl;

      // Determine the attachment path/URL
      const attachmentPath = attachment.path || attachment.url || attachment.file_url;

      if (!attachmentPath) {
        throw new Error('No download path available for attachment');
      }


      // In demo environment with a direct URL available, use it
      if (isDemoEnvironment() && (attachment.url || attachment.file_url)) {

        downloadUrl = attachment.url || attachment.file_url;
      } else {
        // Otherwise, use the path-based approach (works for both prod and demo with paths)
        downloadUrl = await this.getAttachmentDownloadUrl(attachmentPath);
      }

      // Fetch the file
      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error('Download failed');

      // Get the blob from the response
      const blob = await response.blob();

      // Create object URL for the blob
      const objectUrl = window.URL.createObjectURL(blob);

      // Create and trigger download
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = attachment.name || attachment.filename || 'download'; // Fallback filename
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('[PatientAPI] Failed to download attachment:', error);
      throw error;
    }
  }


  // Attachments
  async uploadMessageAttachment(file, messageId, threadId) {
    const fileToBase64 = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      });
    };

    const base64Data = await fileToBase64(file);

    const response = await this.request('/message_attachment', {
      method: 'POST',
      body: JSON.stringify({
        message_id: messageId,
        thread_id: threadId,
        file_name: file.name,
        file_size: file.size,
        mime_type: file.type,
        file_data_string: base64Data,
        uploaded_by: this.getToken() ? 'patient' : null,
        created_at: Date.now(),
        scan_status: 'pending',
        // Include environment flag for backend to handle storage appropriately
        private_storage: !isDemoEnvironment()
      })
    }, 'uploadMessageAttachment');

    // Ensure we return the full response including path
    return response;
  }

  async getMessageAttachments(messageId) {
    return this.request(`/message_attachment?message_id=${messageId}`, {
      method: 'GET'
    }, 'getMessageAttachments');
  }

  async deleteMessageAttachment(attachmentId) {
    return this.request(`/message_attachment/${attachmentId}`, {
      method: 'DELETE'
    }, 'deleteMessageAttachment');
  }

  // Patient-specific: Get care team members they can message
  async getCareTeam() {
    return this.request('/patients/care-team', {
      method: 'GET'
    }, 'getCareTeam');
  }

  // Helper method for sending message with attachments
  async sendMessageWithAttachments(threadId, messageData, files = []) {
    try {
      // Step 1: Create the message
      const messageResponse = await this.sendMessage(threadId, messageData);

      // Step 2: Upload attachments if any
      if (files.length > 0) {
        const attachmentPromises = files.map(file =>
          this.uploadMessageAttachment(file, messageResponse.id, threadId)
        );

        const attachmentResults = await Promise.all(attachmentPromises);

        // Optional: Update message with attachment IDs
        await this.request(`/message/${messageResponse.id}`, {
          method: 'PATCH',
          body: JSON.stringify({
            contains_attachments: true
          })
        }, 'updateMessageAttachments');
      }

      return messageResponse;
    } catch (error) {
      console.error('Error sending message with attachments:', error);
      throw error;
    }
  }

  // ==================== ORDERS ====================

  // Get patient's orders (provider orders + lab orders combined)
  async getMyOrders() {
    return this.request('/patients/orders', {
      method: 'GET'
    });
  }

  // ==================== DOCUMENTS ====================

  // Get patient's documents
  async getMyDocuments() {
    return this.request('/patients/documents', {
      method: 'GET'
    });
  }

  // Upload document
  async uploadDocument(formData) {
    return this.request('/patient/documents', {
      method: 'POST',
      body: formData // FormData for file upload
    });
  }

  // ================ Enabled Features ====================
  // Get patient features based on provider's solutions
  async getFeatures() {
    return this.request('/patients/features', {
      method: 'GET'
    }, { priority: PRIORITY.HIGH, label: 'getFeatures' });
  }

  // ==================== VIDEO SESSIONS ====================

  async getVideoSessionByAppointment(appointmentId) {
    return this.request(`/video_session?appointment_id=${appointmentId}`, {
      method: 'GET'
    });
  }

  async getVideoSession(sessionId) {
    return this.request(`/video_session/${sessionId}`, {
      method: 'GET'
    });
  }

  async joinVideoSession(sessionId) {
    return this.request(`/video_session/${sessionId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        patient_joined_at: Date.now(),
        status: 'active'
      })
    });
  }

  async leaveVideoSession(sessionId) {
    return this.request(`/video_session/${sessionId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        patient_left_at: Date.now()
      })
    });
  }

  // ==================== BILLING ENDPOINTS ====================

  // Get patient's invoices with pagination
  async getMyInvoices(params = {}) {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page);
    if (params.perPage) queryParams.append('per_page', params.perPage || '20');
    if (params.status) queryParams.append('status', params.status);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);

    const queryString = queryParams.toString();
    const endpoint = `/patients/invoices${queryString ? `?${queryString}` : ''}`;

    return this.request(endpoint, {
      method: 'GET'
    });

    /* Expected response:
    {
      items: Invoice[],
      total: number,
      page: number,
      per_page: number,
      total_pages: number
    }
    */
  }

  // Get single invoice detail
  async getInvoiceDetail(invoiceId) {
    return this.request(`/billing/invoices/${invoiceId}`, {
      method: 'GET'
    });

    /* Expected response: Invoice object (as defined in models.ts) */
  }

  // Get count of unpaid invoices (for badge)
  async getUnpaidInvoiceCount() {
    try {
      const response = await this.request('/patients/invoices/unpaid-count', {
        method: 'GET'
      }, { priority: PRIORITY.LOW, label: 'getUnpaidInvoiceCount' });
      return response.count || 0;
    } catch (error) {
      console.error('[PatientAPI] Error fetching unpaid invoice count:', error);
      return 0;
    }
  }

  // Create payment intent for an invoice
  async createPaymentIntent(data) {
    return this.request('/billing/create-payment-intent', {
      method: 'POST',
      body: JSON.stringify({
        invoice_id: data.invoice_id,
        payment_method_id: data.payment_method_id, // Optional - for saved cards
        save_payment_method: data.save_payment_method || false
      })
    });

    /* Expected response:
    {
      payment_intent_id: string,
      client_secret: string,
      amount: number,
      currency: string,
      status: string
    }
    */
  }

  // Confirm payment (after frontend processing)
  async confirmPayment(paymentIntentId, invoiceId) {
    return this.request('/billing/confirm-payment', {
      method: 'POST',
      body: JSON.stringify({
        payment_intent_id: paymentIntentId,
        invoice_id: invoiceId
      })
    });

    /* Expected response:
    {
      success: boolean,
      payment: Payment,
      invoice: Invoice, // Updated with new status
      receipt_url: string
    }
    */
  }

  // Get Stripe publishable key and account info
  async getStripeConfig() {
    return this.request('/billing/config', {
      method: 'GET'
    });

    /* Expected response:
    {
      stripe_publishable_key: string,
      stripe_account_id: string, // Connected account ID
      platform_name: string
    }
    */
  }

  // Create setup intent for saving payment method
  async createSetupIntent() {
    return this.request('/billing/create-setup-intent', {
      method: 'POST',
      body: JSON.stringify({})
    });

    /* Expected response:
    {
      setup_intent_id: string,
      client_secret: string,
      customer_id: string
    }
    */
  }

  // Get saved payment methods
  async getMyPaymentMethods() {
    return this.request('/patients/payment-methods', {
      method: 'GET'
    });

    /* Expected response:
    {
      payment_methods: PaymentMethod[],
      default_payment_method_id: string | null
    }
    */
  }

  // Set default payment method
  async setDefaultPaymentMethod(paymentMethodId) {
    return this.request('/patients/payment-methods/set-default', {
      method: 'POST',
      body: JSON.stringify({
        payment_method_id: paymentMethodId
      })
    });

    /* Expected response:
    {
      success: boolean,
      default_payment_method_id: string
    }
    */
  }

  // Delete payment method
  async deletePaymentMethod(paymentMethodId) {
    return this.request(`/patients/payment-methods?payment_method_id=${paymentMethodId}`, {
      method: 'DELETE'
    });

    /* Expected response:
    {
      success: boolean,
      message: string
    }
    */
  }

  // Get payment history
  async getMyPayments(params = {}) {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page);
    if (params.perPage) queryParams.append('per_page', params.perPage || '20');
    if (params.invoice_id) queryParams.append('invoice_id', params.invoice_id);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);

    const queryString = queryParams.toString();
    const endpoint = `/patients/payments${queryString ? `?${queryString}` : ''}`;

    return this.request(endpoint, {
      method: 'GET'
    });

    /* Expected response:
    {
      items: Payment[],
      total: number,
      page: number,
      per_page: number,
      total_pages: number
    }
    */
  }

  // Download invoice PDF / 
  async downloadInvoicePDF(invoiceId) {
    // This might need special handling for file download
    return this.request(`/billing/invoices/${invoiceId}/download`, {
      method: 'GET'
    });

    /* Expected response: PDF blob or download URL */
  }

  async getMyPackages() {

    return this.request(`/patient_package`, {
      method: 'GET'
    });
  }

  async purchasePackage(purchaseData) {
    return this.request('/patient-packages/purchase', {
      method: 'POST',
      body: JSON.stringify(purchaseData)
    });
  }

  async getAvailablePackages() {

    return this.request(`/service_package`, {
      method: 'GET'
    });
  }

  async getAvailableSubscriptionPlans(filters = {}) {

    return this.request(`/subscription_plan`, {
      method: 'GET'
    });
  }

  async getMySubscriptions() {

    return this.request(`/payment_plan`, {
      method: 'GET'
    });
  }

  async subscribeToPlan(subscriptionData) {
    return this.request('/patient-subscriptions/subscribe', {
      method: 'POST',
      body: JSON.stringify(subscriptionData)
    });
  }

  async cancelSubscription(subscriptionId, cancelAtPeriodEnd = true) {
    return this.request('/patient-subscriptions-cancel', {
      method: 'POST',
      body: JSON.stringify({
        subscription_id: subscriptionId,
        cancel_at_period_end: cancelAtPeriodEnd
      })
    });
  }

  // Ready2Exercise related methods

  async createExerciseAccountPublic(data) {
    // This is a public endpoint - no auth token required
    const response = await fetch(`${this.baseUrl}/public/create-account`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        phone: data.phone,
        birthday: data.birthday,
        referral_code: data.referral_code || undefined,
        routine_key: data.routine_key || undefined,
        proceed_with_partial_match: data.proceed_with_partial_match || false,
        // Platform identifier for backend to set up correct defaults
        platform: 'exercise',
        solution_ids: data.solution_ids || []
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Failed to create account');
    }

    return response.json();

    /* Expected success response:
    {
      success: true,
      user_id: string,
      message: 'Account created successfully'
    }
    
    Expected match response:
    {
      success: false,
      match_type: 'exact' | 'partial',
      message: string
    }
    */
  }

  /**
   * Validate an access code for exercise app
   * 
   * @param {string} code - The access code to validate
   * @param {string} userId - The user ID to validate for
   * @returns {Promise<Object>} Validation result
   */
  async validateExerciseAccessCode(code, userId) {
    return this.request('/exercise/validate-access-code', {
      method: 'POST',
      body: JSON.stringify({
        code: code,
        user_id: userId
      })
    });

    /* Expected response:
    {
      valid: boolean,
      type: 'full_access' | 'discount' | 'trial_extension',
      percent?: number, // For discount codes
      days?: number, // For trial extension
      stripe_coupon_id?: string,
      add_org?: boolean,
      org_id?: string,
      org_name?: string
    }
    */
  }

  /**
   * Create a Stripe Checkout session for the Ready2Exercise subscription
   * ($20/month, 7-day trial, card collected up front). The user is identified server-side ($auth).
   *
   * @param {string} userEmail - Prefills the Checkout email
   * @param {'/onboarding' | '/profile' | '/subscribe'} returnPath - App path Stripe returns to (with ?payment=success|cancelled)
   * @returns {Promise<Object>} Checkout session info
   */
  async createExerciseCheckoutSession(userEmail, returnPath = '/onboarding') {
    return this.request('/exercise/create-checkout-session', {
      method: 'POST',
      body: JSON.stringify({
        user_email: userEmail,
        solution_id: 2,
        return_path: returnPath
      })
    });

    /* Expected response:
    {
      checkout_url: string,
      session_id: string
    }
    */
  }

  /**
   * Open the Stripe Customer Portal for the current user's Ready2Exercise subscription
   * (cancel, update card, invoices). Requires exercise_app.stripe_customer_id.
   *
   * @returns {Promise<Object>} { url }
   */
  async createExerciseBillingPortalSession() {
    return this.request('/exercise/billing-portal-session', {
      method: 'POST',
      body: JSON.stringify({})
    });

    /* Expected response:
    {
      url: string
    }
    */
  }

  /**
   * Generate a personalized 2-week plan from the curated exercise ladder (one AI call, synchronous).
   * The patient is taken from the auth token; the profile comes from patient.fitness_profile.
   * Completes any active workout plan assignment.
   * @param {string} [notes] - Optional free-text notes from the patient
   * @returns {Promise<{success: boolean, workout_plan_id: string, assignment_id: string, plan_name: string, replaced_assignment_id: string}>}
   */
  async generatePlan(notes) {
    return this.request('/exercises/generate-plan', {
      method: 'POST',
      body: JSON.stringify({ notes: notes || '' })
    });
  }

  /**
   * Assign a static base routine (upper | lower | full) built from the exercise ladder at rung 1.
   * Completes any active workout plan assignment.
   * @param {'upper'|'lower'|'full'} routineKey
   * @returns {Promise<{success: boolean, workout_plan_id: string, assignment_id: string, routine_key: string, replaced_assignment_id: string}>}
   */
  async claimBaseRoutine(routineKey) {
    return this.request('/exercises/claim-base-routine', {
      method: 'POST',
      body: JSON.stringify({ routine_key: routineKey })
    });
  }

  /**
   * Start the next 2-week block of the active plan. Rules-based progression from the difficulty
   * ratings logged during the block; no AI call.
   * @param {string} assignmentId
   * @returns {Promise<{success: boolean, workout_plan_id: string, assignment_id: string, block: number, changes: Array}>}
   */
  async advanceBlock(assignmentId) {
    return this.request('/exercises/advance-block', {
      method: 'POST',
      body: JSON.stringify({ assignment_id: assignmentId })
    });
  }

  /**
   * Validate a referral code for the /join/:code landing page. Public endpoint.
   * @param {string} code
   * @returns {Promise<{valid: boolean, code?: string, label?: string, default_routine_key?: string|null, grants_access?: boolean}>}
   */
  async getReferralCode(code) {
    const response = await fetch(`${this.baseUrl}/public/referral-code/${encodeURIComponent(code)}`);
    if (!response.ok) {
      return { valid: false };
    }
    return response.json();
  }

  /**
   * Generate an ad-hoc "Exercise Now" workout based on user selections
   *
   * @param {Object} params - Workout generation parameters
   * @param {string} params.track - Exercise track (general_fitness, weight_loss, muscle_building, strength, cardio, flexibility, hiit)
   * @param {string} params.workout_type - Specific workout type within the track
   * @param {string} params.location_id - Patient location ID for equipment context
   * @param {Array<string>} params.equipment - Available equipment at the location
   * @param {number} params.duration_minutes - Desired workout duration (15, 20, 30, 45, 60, 90)
   * @param {string} params.fitness_level - User's fitness level (beginner, intermediate, advanced)
   * @returns {Promise<Object>} Generated workout with assignment info
   */
  async generateExerciseNowWorkout(params) {
    return this.request('/exercise/generate-now', {
      method: 'POST',
      body: JSON.stringify({
        track: params.track,
        workout_type: params.workout_type,
        location_id: params.location_id,
        equipment: params.equipment,
        duration_minutes: params.duration_minutes,
        fitness_level: params.fitness_level
      })
    });

    /* Expected response:
    {
      success: boolean,
      assignment_id: string,
      prescription_id: string,
      workout: {
        id: string,
        name: string,
        description: string,
        estimated_duration_minutes: number,
        modules: ExercisePrescription['modules']
      },
      message?: string
    }
    */
  }


  // ==================== PROGRESS TRACKING ENDPOINTS ====================

  /**
   * Get paginated session history across all patient assignments
   *
   * @param {Object} params - Query parameters
   * @param {string} params.start_date - Optional start date filter (YYYY-MM-DD)
   * @param {string} params.end_date - Optional end date filter (YYYY-MM-DD)
   * @param {string} params.assignment_id - Optional specific assignment filter
   * @param {number} params.page - Page number (default 1)
   * @param {number} params.per_page - Items per page (default 20)
   * @returns {Promise<Object>} Paginated session history
   */
  async getProgressSessions(params = {}) {
    const queryParams = new URLSearchParams();

    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.assignment_id) queryParams.append('assignment_id', params.assignment_id);
    if (params.page) queryParams.append('page', params.page);
    if (params.per_page) queryParams.append('per_page', params.per_page || '20');

    const queryString = queryParams.toString();
    const endpoint = `/patients/progress/sessions${queryString ? `?${queryString}` : ''}`;

    return this.request(endpoint, {
      method: 'GET'
    });

    /* Expected response:
    {
      sessions: SessionCompletion[],
      total: number,
      page: number,
      per_page: number,
      total_pages: number
    }
    */
  }

  /**
   * Get aggregate progress statistics for the patient
   *
   * @param {Object} params - Query parameters
   * @param {string} params.start_date - Optional start date filter
   * @param {string} params.end_date - Optional end date filter
   * @returns {Promise<Object>} Progress summary stats
   */
  async getProgressSummary(params = {}) {
    const queryParams = new URLSearchParams();

    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);

    const queryString = queryParams.toString();
    const endpoint = `/patients/progress/summary${queryString ? `?${queryString}` : ''}`;

    return this.request(endpoint, {
      method: 'GET'
    });

    /* Expected response:
    {
      total_sessions: number,
      total_exercises: number,
      total_minutes: number,
      current_streak: number,
      sessions_this_week: number,
      last_session_date: string | null
    }
    */
  }

  /**
   * Get detailed session info with exercise completions
   *
   * @param {string} sessionId - The session ID
   * @returns {Promise<Object>} Session detail with exercise completions
   */
  async getProgressSessionDetail(sessionId) {
    return this.request(`/patients/progress/session/${sessionId}`, {
      method: 'GET'
    });

    /* Expected response:
    {
      session: SessionCompletion,
      exercise_completions: ExerciseCompletion[]
    }
    */
  }

  // ==================== DAY SWAPPING ENDPOINTS ====================

  /**
   * Create a schedule override to swap workout days
   *
   * @param {string} assignmentId - The assignment ID
   * @param {Object} overrideData - Override data
   * @param {number} overrideData.week_number - Which week (1, 2, 3...)
   * @param {number} overrideData.original_day - ISO day (1=Mon, 7=Sun)
   * @param {number} overrideData.new_day - ISO day to swap to
   * @returns {Promise<Object>} Updated assignment with overrides
   */
  async createScheduleOverride(assignmentId, overrideData) {
    return this.request(`/patients/exercise-assignments/${assignmentId}/schedule-override`, {
      method: 'PATCH',
      body: JSON.stringify({
        week_number: overrideData.week_number,
        original_day: overrideData.original_day,
        new_day: overrideData.new_day
      })
    });
  }

  /**
   * Remove a schedule override
   *
   * @param {string} assignmentId - The assignment ID
   * @param {Object} overrideData - Override to remove
   * @param {number} overrideData.week_number - Which week
   * @param {number} overrideData.original_day - Original ISO day
   * @returns {Promise<Object>} Updated assignment
   */
  async deleteScheduleOverride(assignmentId, overrideData) {
    return this.request(`/patients/exercise-assignments/${assignmentId}/schedule-override`, {
      method: 'DELETE',
      body: JSON.stringify({
        week_number: overrideData.week_number,
        original_day: overrideData.original_day
      })
    });
  }

  /**
   * Clear all schedule overrides for an assignment (reset to original schedule)
   *
   * @param {string} assignmentId - The assignment ID
   * @param {number} weekNumber - Optional: only clear overrides for a specific week
   * @returns {Promise<Object>} Updated assignment
   */
  async clearScheduleOverrides(assignmentId, weekNumber = null) {
    const body = weekNumber !== null ? { week_number: weekNumber } : {};
    return this.request(`/patients/exercise-assignments/schedule-override/clear/${assignmentId}`, {
      method: 'POST',
      body: JSON.stringify(body)
    });
  }

  // ==================== PATIENT LOCATIONS ====================

  /**
   * Get all patient locations (own + favorited public)
   * @returns {Promise<Array>} List of patient locations
   */
  async getMyLocations() {
    return this.request('/patients/locations', {
      method: 'GET'
    });
  }

  /**
   * Get single location details
   * @param {string} locationId - The location ID
   * @returns {Promise<Object>} Location details
   */
  async getLocation(locationId) {
    return this.request(`/patients/locations/${locationId}`, {
      method: 'GET'
    });
  }

  /**
   * Create new patient-owned location
   * @param {Object} locationData - Location data
   * @returns {Promise<Object>} Created location
   */
  async createLocation(locationData) {
    return this.request('/patients/locations', {
      method: 'POST',
      body: JSON.stringify(locationData)
    });
  }

  /**
   * Update a location
   * @param {string} locationId - The location ID
   * @param {Object} updateData - Fields to update
   * @returns {Promise<Object>} Updated location
   */
  async updateLocation(locationId, updateData) {
    return this.request(`/patients/locations/${locationId}`, {
      method: 'PATCH',
      body: JSON.stringify(updateData)
    });
  }

  /**
   * Delete a location
   * @param {string} locationId - The location ID
   * @returns {Promise<void>}
   */
  async deleteLocation(locationId) {
    return this.request(`/patients/locations/${locationId}`, {
      method: 'DELETE'
    });
  }

  /**
   * Set a location as the default for sessions
   * @param {string} locationId - The location ID
   * @returns {Promise<Object>} Updated location
   */
  async setDefaultLocation(locationId) {
    return this.request(`/patients/locations/${locationId}/set-default`, {
      method: 'POST'
    });
  }

  /**
   * Search public gyms (for adding to favorites)
   * @param {string} query - Search query
   * @param {Object} filters - Optional filters (city, state, etc.)
   * @returns {Promise<Array>} List of matching public locations
   */
  async searchPublicGyms(query, filters = {}) {
    const params = new URLSearchParams({ q: query, ...filters });
    return this.request(`/locations/public?${params}`, {
      method: 'GET'
    });
  }

  /**
   * Add public gym to patient's favorites
   * @param {string} locationId - The public location ID
   * @param {Object} options - Optional settings (nickname, is_default)
   * @returns {Promise<Object>} Created favorite record
   */
  async addPublicGymToFavorites(locationId, options = {}) {
    return this.request('/patients/locations/favorites', {
      method: 'POST',
      body: JSON.stringify({
        location_id: locationId,
        ...options
      })
    });
  }

  /**
   * Remove public gym from favorites
   * @param {string} locationId - The public location ID
   * @returns {Promise<void>}
   */
  async removePublicGymFromFavorites(locationId) {
    return this.request(`/patients/locations/favorites/${locationId}`, {
      method: 'DELETE'
    });
  }

  // ==================== EXERCISE SUBSTITUTION ====================

  /**
   * Batch check exercises for equipment compatibility
   * @param {Array<string>} exerciseIds - List of exercise IDs to check
   * @param {Array<string>} availableEquipment - Equipment available at location
   * @returns {Promise<Object>} Compatibility results with compatible/incompatible lists
   */
  async checkExerciseEquipment(exerciseIds, availableEquipment) {
    return this.request('/exercises/check-equipment', {
      method: 'POST',
      body: JSON.stringify({
        exercise_ids: exerciseIds,
        available_equipment: availableEquipment
      })
    });
  }

  /**
   * Find substitute exercises based on equipment availability
   * @param {Object} params - Search parameters
   * @param {string} params.exercise_id - Original exercise ID
   * @param {Array<string>} params.available_equipment - Available equipment
   * @param {number} params.difficulty_tolerance - How much difficulty variance (default: 2)
   * @param {boolean} params.include_bodyweight - Include bodyweight alternatives (default: true)
   * @param {number} params.limit - Max substitutes to return (default: 5)
   * @returns {Promise<Object>} Original exercise info and substitute options
   */
  async findExerciseSubstitutes(params) {
    return this.request('/exercises/find-substitutes', {
      method: 'POST',
      body: JSON.stringify({
        exercise_id: params.exercise_id,
        available_equipment: params.available_equipment,
        difficulty_tolerance: params.difficulty_tolerance ?? 2,
        include_bodyweight: params.include_bodyweight ?? true,
        limit: params.limit ?? 5
      })
    });
  }

  /**
   * Batch find substitutes for multiple exercises
   * @param {Array<string>} exerciseIds - List of exercise IDs needing substitutes
   * @param {Array<string>} availableEquipment - Equipment available at location
   * @returns {Promise<Array>} Substitute options for each exercise
   */
  async findBatchSubstitutes(exerciseIds, availableEquipment) {
    return this.request('/exercises/find-substitutes/batch', {
      method: 'POST',
      body: JSON.stringify({
        exercise_ids: exerciseIds,
        available_equipment: availableEquipment
      })
    });
  }

  // ============ Classes ============

  async getAvailableClasses(filters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, val]) => {
      if (val !== undefined && val !== null) params.append(key, val);
    });
    return this.request(`/patients/class-definitions?${params.toString()}`, { method: 'GET' });
  }

  async getClassSessions(classDefinitionId, filters = {}) {
    const params = new URLSearchParams({ class_definition_id: classDefinitionId });
    Object.entries(filters).forEach(([key, val]) => {
      if (val !== undefined && val !== null) params.append(key, val);
    });
    return this.request(`/patients/class-sessions?${params.toString()}`, { method: 'GET' });
  }

  async enrollInClass(data) {
    return this.request('/class_enrollment', {
      method: 'POST',
      body: JSON.stringify({ ...data, enrollment_source: 'self_booked' })
    });
  }

  /**
   * Enroll in one session as a single purchase. Creates the class invoice server-side;
   * returns { enrollment, invoice_id, amount, class_name, session_date }. invoice_id is null
   * when waitlisted (invoiced on promotion). Pay via /billing/pay/:invoice_id.
   */
  async purchaseClassEnrollment(classSessionId, savePaymentMethod = false) {
    return this.request('/class-enrollment-purchase', {
      method: 'POST',
      body: JSON.stringify({
        class_session_id: classSessionId,
        save_payment_method: savePaymentMethod
      })
    });
  }

  async cancelClassEnrollment(enrollmentId) {
    return this.request(`/class_enrollment/${enrollmentId}/cancel`, { method: 'POST' });
  }

  async getMyClassEnrollments() {
    return this.request('/patients/class-enrollments', { method: 'GET' });
  }

  async getMyClassMemberships() {
    return this.request('/class_membership', { method: 'GET' });
  }

  async createClassMembership(data) {
    return this.request('/class_membership', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async cancelClassMembership(membershipId) {
    return this.request(`/class_membership/${membershipId}/cancel`, { method: 'POST' });
  }

}

const patientApiService = new PatientApiService();

export { patientApiService };